import React, { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import tw, { styled } from 'twin.macro';
import { RootState } from '../../../../app/store/store.app';
import { RouteConfig, sidebarConfig } from '../../../../config/router/router.config';
import { routeLevelChecker } from '../../../../util/wrapper/wrapper.util';
import useTranslator from '../../../../hook/common/useTranslator/useTranslator.hook';

const NavBar = tw.nav`fixed bottom-0 left-0 right-0 z-40 flex h-16 items-stretch border-t border-gray-100 bg-white`;
const NavButton = styled.button(({ $active }: { $active: boolean }) => [
  tw`flex flex-1 flex-col items-center justify-center gap-0.5 py-2 transition-colors`,
  $active ? tw`text-primary-500` : tw`text-gray-400`,
]);
const NavLabel = styled.span(({ $active }: { $active: boolean }) => [
  tw`text-[10px] leading-none font-inter`,
  $active ? tw`text-primary-500 font-semibold` : tw`text-gray-400`,
]);

function firstAvailablePath(route: RouteConfig, level?: number): string {
  const subRoutes = route.options?.hasSub?.filter((item) =>
    routeLevelChecker({ route: item, level }),
  );
  if (!subRoutes?.length) return route.path;
  return subRoutes[0].path;
}

export default function MobileNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const translator = useTranslator();
  const { level } = useSelector(
    (state: RootState) => state.session,
    shallowEqual,
  );

  const navItems = useMemo(
    () =>
      sidebarConfig
        .filter((route) => route.options && routeLevelChecker({ route, level }))
        .map((route) => ({
          path: route.path,
          label: route.options?.label ?? route.name,
          icon: route.options?.icon,
          goTo: firstAvailablePath(route, level),
        })),
    [level],
  );

  return (
    <NavBar>
      {navItems.map((item) => {
        const active =
          pathname === item.path || pathname.startsWith(`${item.path}/`);
        return (
          <NavButton
            key={item.path}
            $active={active}
            onClick={() => navigate(item.goTo)}
          >
            {item.icon}
            <NavLabel $active={active}>
              {translator.translate(item.label)}
            </NavLabel>
          </NavButton>
        );
      })}
    </NavBar>
  );
}
